/**
 * Named directory list cache invalidation (branding HMAC refresh on signed URL fail).
 */

import type { QueryClient } from '@tanstack/react-query';
import { customerQueryKeys } from './queryKeys.js';

export function invalidatePublicCustomerTenantsCache(
  queryClient: QueryClient,
  apiBaseUrl: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: customerQueryKeys.publicCustomerTenants(apiBaseUrl),
  });
}

/**
 * Prefix-invalidate all public sales-point directory queries for this API base
 * (platform + every tenant filter + Favorites listable-allowlist). Used after
 * favorite toggles when shops persist sort is `favoriteDesc`, and whenever
 * Obchody directory freshness must propagate to Favorites (G7).
 */
export function invalidatePublicCustomerSalesPointsCache(
  queryClient: QueryClient,
  apiBaseUrl: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: ['public', 'customer-sales-points', apiBaseUrl],
  });
}

export function invalidateSalesPointContactsCache(
  queryClient: QueryClient,
  customerKey: number | string,
): void {
  void queryClient.invalidateQueries({
    queryKey: customerQueryKeys.salesPointContacts(customerKey),
  });
}

/**
 * Prefix-invalidate every signed-in contacts query (any customerKey).
 * Used when branding SSE fires without a customerKey in scope (G7 Obíbené thumbs).
 */
export function invalidateAllSalesPointContactsCache(
  queryClient: QueryClient,
): void {
  void queryClient.invalidateQueries({
    queryKey: ['customer', 'salesPointContacts'],
  });
}

export function invalidateAccountMembershipsCache(
  queryClient: QueryClient,
  tenantCode: string,
  customerId: number,
): void {
  void queryClient.invalidateQueries({
    queryKey: customerQueryKeys.accountMemberships(tenantCode, customerId),
  });
}

/**
 * Invalidate every account memberships query (tenant-scoped + platform).
 * Branding SSE has no tenantCode/customerId in scope; memberships DTO carries
 * logoUrl + dual-regime rim (HMAC / future thumb consumers).
 */
export function invalidateAllAccountMembershipsCache(
  queryClient: QueryClient,
): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key.length === 5 &&
        key[0] === 'customer' &&
        key[2] === 'account' &&
        key[4] === 'memberships'
      );
    },
  });
}

/**
 * Prefix-invalidate every tenant surface-entitlement snapshot (G12 live-sync).
 * Directory SSE has no tenant in scope — entitlements carry promotions/loyalty blocks.
 */
export function invalidateAllSurfaceEntitlementCache(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key.length === 3 &&
        key[0] === 'customer' &&
        key[2] === 'surfaceEntitlement'
      );
    },
  });
}

/**
 * Prefix-invalidate checkout pickup-point lists for every tenant + kiosk (G12).
 */
export function invalidateAllKioskPickupPointsCache(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key.length === 4 &&
        key[0] === 'customer' &&
        key[2] === 'kioskPickupPoints'
      );
    },
  });
}

/**
 * Cross-tenant sales-point scoped pickup list refresh when directory SSE carries
 * `salesPointId` (pickup_points_updated).
 */
export function invalidateKioskPickupPointsForSalesPoint(
  queryClient: QueryClient,
  salesPointId: number,
): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key.length === 4 &&
        key[0] === 'customer' &&
        key[2] === 'kioskPickupPoints' &&
        key[3] === salesPointId
      );
    },
  });
}

/**
 * Prefix-invalidate tenant promo rewards/events/pricing caches (G12 promotions config).
 */
export function invalidateAllPromoTenantQueriesCache(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key.length >= 4 &&
        key[0] === 'customer' &&
        key[2] === 'promo'
      );
    },
  });
}
